import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostListener,
  ElementRef,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

export interface CalendarDay {
  date: Date;
  otherMonth: boolean;
}

export interface DateRange {
  from: string;
  to: string;
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = 'Date';
  @Input() placeholder: string = 'Select a date';
  @Input() mode: 'single' | 'range' = 'single';
  @Input() minDate: string = '';
  @Input() maxDate: string = '';
  @Input() disabled: boolean = false;

  @Output() dateChange = new EventEmitter<string | DateRange>();

  // ── State ──────────────────────────────────────────────────────────────────
  isOpen: boolean = false;

  // single mode
  selectedDate: Date | null = null;

  // range mode
  rangeStart: Date | null = null;
  rangeEnd: Date | null = null;
  hoverDate: Date | null = null;
  selectingEnd: boolean = false; // true after first click in range mode

  viewYear: number = new Date().getFullYear();
  viewMonth: number = new Date().getMonth();
  viewMode: 'days' | 'months' | 'years' = 'days';

  calendarDays: CalendarDay[] = [];
  yearRange: number[] = [];

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  readonly MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  readonly SHORT_MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  readonly WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.buildCalendar();
    this.buildYearRange();
  }

  // ── Getters ────────────────────────────────────────────────────────────────
  get todayDate(): Date { return new Date(); }

  get hasValue(): boolean {
    return this.mode === 'single'
      ? !!this.selectedDate
      : !!(this.rangeStart && this.rangeEnd);
  }

  get displayValue(): string {
    if (this.mode === 'single') {
      return this.selectedDate ? this.formatDate(this.selectedDate) : '';
    }
    if (this.rangeStart && this.rangeEnd) {
      return `${this.formatDate(this.rangeStart)}  →  ${this.formatDate(this.rangeEnd)}`;
    }
    if (this.rangeStart) {
      return `${this.formatDate(this.rangeStart)}  →  ...`;
    }
    return '';
  }

  get rangePlaceholder(): string {
    if (this.selectingEnd && this.rangeStart) return 'Pick end date…';
    return 'Select date range';
  }

  // ── ControlValueAccessor ───────────────────────────────────────────────────
  writeValue(value: any): void {
    if (!value) {
      this.selectedDate = null;
      this.rangeStart = null;
      this.rangeEnd = null;
      return;
    }
    if (this.mode === 'single') {
      const d = new Date(value as string);
      if (!isNaN(d.getTime())) {
        this.selectedDate = d;
        this.viewYear = d.getFullYear();
        this.viewMonth = d.getMonth();
        this.buildCalendar();
      }
    } else {
      const range = value as DateRange;
      if (range.from) {
        const f = new Date(range.from);
        if (!isNaN(f.getTime())) {
          this.rangeStart = f;
          this.viewYear = f.getFullYear();
          this.viewMonth = f.getMonth();
          this.buildCalendar();
        }
      }
      if (range.to) {
        const t = new Date(range.to);
        if (!isNaN(t.getTime())) this.rangeEnd = t;
      }
    }
  }

  registerOnChange(fn: (v: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // ── Calendar builders ──────────────────────────────────────────────────────
  buildCalendar(): void {
    const year = this.viewYear;
    const month = this.viewMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days: CalendarDay[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), otherMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: new Date(year, month, d), otherMonth: false });
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push({ date: new Date(year, month + 1, d), otherMonth: true });
    }
    this.calendarDays = days;
  }

  buildYearRange(): void {
    const base = Math.floor(this.viewYear / 12) * 12;
    this.yearRange = Array.from({ length: 12 }, (_, i) => base + i);
  }

  // ── Calendar open/close ────────────────────────────────────────────────────
  toggleCalendar(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const anchor = this.selectedDate ?? this.rangeStart ?? new Date();
      this.viewYear = anchor.getFullYear();
      this.viewMonth = anchor.getMonth();
      this.viewMode = 'days';
      this.buildCalendar();
      this.buildYearRange();
    }
    this.onTouched();
  }

  // ── Day selection ──────────────────────────────────────────────────────────
  selectDay(day: CalendarDay): void {
    if (this.isDateDisabled(day.date)) return;

    if (this.mode === 'single') {
      this.selectedDate = day.date;
      const iso = this.toISO(day.date);
      this.onChange(iso);
      this.dateChange.emit(iso);
      this.isOpen = false;
    } else {
      this.handleRangeSelect(day.date);
    }
  }

  private handleRangeSelect(date: Date): void {
    // No start selected yet, or we're resetting
    if (!this.selectingEnd) {
      this.rangeStart = date;
      this.rangeEnd = null;
      this.selectingEnd = true;
      return;
    }

    // Second click — finalise range
    let start = this.rangeStart!;
    let end = date;

    // swap if end is before start
    if (end < start) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    this.rangeStart = start;
    this.rangeEnd = end;
    this.selectingEnd = false;
    this.hoverDate = null;

    const value: DateRange = { from: this.toISO(start), to: this.toISO(end) };
    this.onChange(value);
    this.dateChange.emit(value);
    this.isOpen = false;
  }

  onDayHover(date: Date): void {
    if (this.mode === 'range' && this.selectingEnd) {
      this.hoverDate = date;
    }
  }

  onDayLeave(): void {
    this.hoverDate = null;
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  prevMonth(): void {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
    else { this.viewMonth--; }
    this.buildCalendar();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
    else { this.viewMonth++; }
    this.buildCalendar();
  }

  prevYear(): void { this.viewYear--; this.buildCalendar(); }
  nextYear(): void { this.viewYear++; this.buildCalendar(); }
  prevYearPage(): void { this.viewYear -= 12; this.buildYearRange(); }
  nextYearPage(): void { this.viewYear += 12; this.buildYearRange(); }

  selectMonth(i: number): void {
    this.viewMonth = i;
    this.viewMode = 'days';
    this.buildCalendar();
  }

  selectYear(year: number): void {
    this.viewYear = year;
    this.viewMode = 'months';
    this.buildYearRange();
  }

  setViewMode(mode: 'days' | 'months' | 'years'): void {
    this.viewMode = mode;
    if (mode === 'years') this.buildYearRange();
  }

  // ── Clear ──────────────────────────────────────────────────────────────────
  clearDate(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedDate = null;
    this.rangeStart = null;
    this.rangeEnd = null;
    this.selectingEnd = false;
    this.hoverDate = null;
    const empty = this.mode === 'single' ? '' : { from: '', to: '' };
    this.onChange(empty);
    this.dateChange.emit(empty as any);
  }

  // ── Day state helpers ──────────────────────────────────────────────────────
  isSelected(date: Date): boolean {
    if (this.mode === 'single') {
      return !!this.selectedDate && this.isSameDay(this.selectedDate, date);
    }
    return this.isRangeStart(date) || this.isRangeEnd(date);
  }

  isRangeStart(date: Date): boolean {
    return !!this.rangeStart && this.isSameDay(this.rangeStart, date);
  }

  isRangeEnd(date: Date): boolean {
    return !!this.rangeEnd && this.isSameDay(this.rangeEnd, date);
  }

  isInRange(date: Date): boolean {
    if (this.mode !== 'range') return false;

    const start = this.rangeStart;
    // While selecting: use hover to preview
    const end = this.rangeEnd ?? this.hoverDate;

    if (!start || !end) return false;
    const [lo, hi] = start <= end ? [start, end] : [end, start];
    return date > lo && date < hi;
  }

  isHoverEdge(date: Date): boolean {
    if (this.mode !== 'range' || !this.selectingEnd) return false;
    return !!this.hoverDate && this.isSameDay(this.hoverDate, date);
  }

  isToday(date: Date): boolean { return this.isSameDay(new Date(), date); }

  isDateDisabled(date: Date): boolean {
    const iso = this.toISO(date);
    if (this.minDate && iso < this.minDate) return true;
    if (this.maxDate && iso > this.maxDate) return true;
    return false;
  }

  // ── Private helpers ────────────────────────────────────────────────────────
  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private toISO(d: Date): string {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  }

  private formatDate(d: Date): string {
    return `${d.getDate().toString().padStart(2, '0')} ${this.SHORT_MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
      if (this.mode === 'range' && this.selectingEnd && !this.rangeEnd) {
        // cancel partial range selection
        this.rangeStart = null;
        this.selectingEnd = false;
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
    if (this.mode === 'range' && this.selectingEnd && !this.rangeEnd) {
      this.rangeStart = null;
      this.selectingEnd = false;
    }
  }
}
